// src/pages/ArabicFlagGuesser.js
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants adapted for flag guessing
const SCORING_CONSTANTS = {
  BASE_POINTS: 100,
  STREAK_MULTIPLIER: 2,
  MAX_TIME_BONUS: 500,
  MAX_ACCURACY_BONUS: 300,
  PERFECT_ACCURACY_BONUS: 500,
  IDEAL_TIME_PER_QUESTION: 15, // seconds
  HINT_PENALTY: 50, // points deducted per hint used
};

const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-40 bg-gray-300 rounded mb-4"></div>
    <div className="h-8 bg-gray-300 rounded mb-2"></div>
    <div className="h-8 bg-gray-300 rounded mb-2"></div>
    <div className="h-8 bg-gray-300 rounded mb-2"></div>
    <div className="h-8 bg-gray-300 rounded"></div>
  </div>
);

const ArabicFlagGuesser = () => {
  // Initialize hooks
  const { saveSessionData } = useAnalytics();
  const { 
    levelsData,
    bestScores,
    isLoading,
    saveLevelData,
    resetAllProgress
  } = useFirebase('arabicFlagGuesser');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  const playSoundFromUrl = useCallback((url) => {
    if (!url) {
      console.log('No URL provided for audio playback');
      return;
    }

    try {
      const audio = new Audio(url);
      audio.volume = 0.6; // Match the effects volume from useAudio
      
      audio.addEventListener('canplaythrough', () => {
        audio.play().catch(e => console.log('URL audio play failed:', e));
      }, { once: true });
      
      audio.addEventListener('error', (e) => {
        console.log('URL audio failed to load:', url, e);
      }, { once: true });
      
      audio.load();
    } catch (e) {
      console.log('Error creating audio from URL:', url, e);
    }
  }, []);

  // Game state
  const [countries, setCountries] = useState([]);
  const [currentCountryIndex, setCurrentCountryIndex] = useState(0);
  const [currentCountry, setCurrentCountry] = useState(null);
  const [options, setOptions] = useState([]);
  const [gameStatus, setGameStatus] = useState('playing');
  const [selectedOption, setSelectedOption] = useState(null);
  const [usedHints, setUsedHints] = useState(0);
  const [showHintWarning, setShowHintWarning] = useState(false);
  const [dataError, setDataError] = useState(null);
  const [flagLoading, setFlagLoading] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [currentHint, setCurrentHint] = useState('');

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Scoring state
  const [totalScore, setTotalScore] = useState(0);
  const [timeBonus, setTimeBonus] = useState(0);
  const [streakBonus, setStreakBonus] = useState(0);
  const [accuracyBonus, setAccuracyBonus] = useState(0);
  const [stars, setStars] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [correctAttempts, setCorrectAttempts] = useState(0);
  const [levelStartTime] = useState(Date.now());
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  const [currentQuestionStartTime, setCurrentQuestionStartTime] = useState(Date.now());
  
  // Modal and effects state
  const [showLevelComplete, setShowLevelComplete] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);

  // Helper functions
  const shuffleArray = useCallback((array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }, []);

  const calculateTimeBonus = useCallback((startTime, questionCount) => {
    const totalTimeSpent = (Date.now() - startTime) / 1000;
    const idealTime = SCORING_CONSTANTS.IDEAL_TIME_PER_QUESTION * questionCount;
    const timeRatio = Math.max(0, (idealTime - totalTimeSpent) / idealTime);
    return Math.round(timeRatio * SCORING_CONSTANTS.MAX_TIME_BONUS);
  }, []);

  const calculateAccuracyBonus = useCallback((correct, total) => {
    if (total === 0) return 0;
    const accuracy = correct / total;
    if (accuracy === 1) return SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS;
    return Math.round(accuracy * SCORING_CONSTANTS.MAX_ACCURACY_BONUS);
  }, []);

  const calculateStars = useCallback((score, questionCount, accuracy) => {
    const maxPossibleScore = questionCount * SCORING_CONSTANTS.BASE_POINTS + SCORING_CONSTANTS.MAX_TIME_BONUS + SCORING_CONSTANTS.PERFECT_ACCURACY_BONUS;
    const scoreRatio = score / maxPossibleScore;
    
    if (accuracy === 100 && scoreRatio >= 0.9) return 3;
    if (accuracy >= 80 && scoreRatio >= 0.7) return 2;
    if (accuracy >= 60) return 1;
    return 0;
  }, []);

  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0) => {
    const colors = isStreak || earnedStars > 0 ? 
      ['#FFD700', '#FFA500', '#FF6347'] : 
      ['#00ff88', '#00ddff', '#ff6b9d'];
    const shapes = earnedStars > 0 ? ['star'] : ['circle'];

    confetti({
      particleCount: earnedStars > 0 ? 120 : 80,
      origin: { y: 0.6 },
      colors,
      shapes,
      gravity: 0.8,
      drift: 0.1
    });

    setCelebrations(true);
    setSparkles(true);

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 2000);
  }, []);

  // Initialize game data with error handling
  useEffect(() => {
    setSessionStartTime(Date.now());
    setSessionMistakes([]);
    if (!levelsData || isLoading) return;

    try {
      const countriesData = Object.values(levelsData)
        .map(country => {
          if (!country || !country.names || !country.countryCode) {
            return null;
          }
          return {
            ...country,
            id: country.countryCode,
            names: {
              english: country.names.english || 'Unknown',
              arabic: country.names.arabic || 'غير معروف',
              pronunciation: country.names.pronunciation || ''
            },
            capital: country.capital || {
              english: 'Unknown',
              arabic: 'غير معروف'
            },
            population: country.population || 0,
            flag: country.flag || '/pics/flags/default.svg',
            audio: country.audio || {}
          };
        })
        .filter(country => country);

      if (countriesData.length === 0) {
        throw new Error('No valid country data found');
      }

      setCountries(prev => prev.length === 0 ? 
        shuffleArray(countriesData) : prev);
      setDataError(null);
    } catch (error) {
      console.error('Error initializing country data:', error);
      setDataError(error.message);
    }
  }, [levelsData, isLoading, shuffleArray]);

  // Set up current question when index changes
  useEffect(() => {
    if (countries.length === 0 || currentCountryIndex >= countries.length) {
      return;
    }

    const country = countries[currentCountryIndex];
    if (!country) {
      return;
    }

    setCurrentCountry(country);
    setCurrentQuestionStartTime(Date.now());
    setSelectedOption(null);
    setGameStatus('playing');
    setUsedHints(0);

    // Generate options - make this more defensive too
    const validCountries = countries.filter(c => c && c.id && c.names);
    const wrongOptions = validCountries
      .filter(c => c.id !== country.id)
      .sort(() => 0.5 - Math.random())
      .slice(0, 3)
      .map(c => ({
        id: c.id,
        english: c.names?.english || 'Unknown',
        arabic: c.names?.arabic || 'غير معروف',
        isCorrect: false
      }));

    const correctOption = {
      id: country.id,
      english: country.names?.english || 'Unknown',
      arabic: country.names?.arabic || 'غير معروف',
      isCorrect: true
    };

    const allOptions = [...wrongOptions, correctOption];
    const shuffledOptions = [...allOptions];
    for (let i = shuffledOptions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffledOptions[i], shuffledOptions[j]] = [shuffledOptions[j], shuffledOptions[i]];
    }
    setOptions(shuffledOptions);
    setShowHint(false);
    setCurrentHint('');
  }, [countries, currentCountryIndex]);

  // Handle hint request
  const handleHintRequest = useCallback(() => {
    if (usedHints >= 2 || gameStatus !== 'playing') return;
    setShowHintWarning(true);
  }, [usedHints, gameStatus]);

  const useHint = useCallback(() => {
    if (!currentCountry) return;

    const hints = [];
    if (currentCountry.capital?.english) {
      hints.push(`Capital: ${currentCountry.capital.english} (${currentCountry.capital.arabic || ''})`);
    }
    if (currentCountry.population) {
      hints.push(`Population: ${currentCountry.population.toLocaleString()}`);
    }

    if (hints.length > 0) {
      const randomHint = hints[Math.floor(Math.random() * hints.length)];
      setCurrentHint(randomHint);
      setShowHint(true);
      setUsedHints(prev => prev + 1);
      setTotalScore(prev => Math.max(0, prev - SCORING_CONSTANTS.HINT_PENALTY));
    }

    setShowHintWarning(false);
  }, [currentCountry]);

  const cancelHint = useCallback(() => {
    setShowHintWarning(false);
  }, []);

  // Handle level completion
  const handleLevelComplete = useCallback(() => {
    if (countries.length === 0) return;

    const finalTimeBonus = calculateTimeBonus(levelStartTime, countries.length);
    const accuracy = Math.round(correctAttempts / Math.max(1, attempts)) * 100;
    const finalAccuracyBonus = calculateAccuracyBonus(correctAttempts, attempts);
    const finalTotalScore = totalScore + finalTimeBonus + finalAccuracyBonus;
    const earnedStars = calculateStars(finalTotalScore, countries.length, accuracy);

    setTimeBonus(finalTimeBonus);
    setAccuracyBonus(finalAccuracyBonus);
    setStars(earnedStars);

    const scoreData = {
      totalScore: finalTotalScore,
      stars: earnedStars,
      accuracy,
      timeBonus: finalTimeBonus,
      streakBonus,
      attempts
    };

    saveLevelData(1, scoreData);

    const sessionData = {
      gameType: 'arabicFlagGuesser',
      level: 1,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: finalTotalScore,
      accuracy,
      attempts,
      correctAnswers: correctAttempts,
      mistakes: sessionMistakes,
      starsEarned: earnedStars,
      timeBonus: finalTimeBonus,
      accuracyBonus: finalAccuracyBonus,
      hintsUsed: usedHints
    };
    saveSessionData(sessionData);

    if (earnedStars > 0) {
      triggerCelebration(false, earnedStars);
    }

    playPerfectSound();
    setShowLevelComplete(true);
  }, [
    countries.length,
    levelStartTime,
    correctAttempts,
    attempts,
    totalScore,
    streakBonus,
    calculateTimeBonus,
    calculateAccuracyBonus,
    calculateStars,
    saveLevelData,
    triggerCelebration,
    playPerfectSound
  ]);

  // Handle option selection
  const handleOptionSelect = useCallback((option) => {
    if (gameStatus !== 'playing' || !currentCountry) return;

    setSelectedOption(option);
    setAttempts(prev => prev + 1);

    if (option.isCorrect) {
      setGameStatus('correct');
      setCorrectAttempts(prev => prev + 1);
      
      const newConsecutive = consecutiveCorrect + 1;
      setConsecutiveCorrect(newConsecutive);
      
      let points = SCORING_CONSTANTS.BASE_POINTS;
      if (newConsecutive >= 3) {
        points *= SCORING_CONSTANTS.STREAK_MULTIPLIER;
        setStreakBonus(prev => prev + (SCORING_CONSTANTS.BASE_POINTS * (SCORING_CONSTANTS.STREAK_MULTIPLIER - 1)));
        triggerCelebration(true);
        playStreakSound();
      } else {
        triggerCelebration(false);
        playCorrectSound();
      }
      
      setTotalScore(prev => prev + points);

      // Play country name audio
      if (currentCountry.audio?.country?.url) {
        playSoundFromUrl(currentCountry.audio.country.url);
      }

      setTimeout(() => {
        if (currentCountryIndex + 1 >= countries.length) {
          handleLevelComplete();
        } else {
          setCurrentCountryIndex(prev => prev + 1);
        }
      }, 3000);
    } else {
      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_flag_guess',
        attempted: option.nameEnglish,
        correct: currentCountry.nameEnglish,
        correctArabic: currentCountry.nameArabic,
        countryIndex: currentCountryIndex
      }]);
      setGameStatus('incorrect');
      setConsecutiveCorrect(0);
      playIncorrectSound();
      
      setTimeout(() => {
        if (currentCountryIndex + 1 >= countries.length) {
          handleLevelComplete();
        } else {
          setCurrentCountryIndex(prev => prev + 1);
        }
      }, 1500);
    }
  }, [
    gameStatus,
    consecutiveCorrect,
    currentCountry,
    currentCountryIndex,
    countries.length,
    triggerCelebration,
    playCorrectSound,
    playStreakSound,
    playIncorrectSound,
    playSoundFromUrl,
    handleLevelComplete
  ]);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    setCurrentCountryIndex(0);
    setTotalScore(0);
    setCorrectAttempts(0);
    setAttempts(0);
    setConsecutiveCorrect(0);
    setShowResetConfirmation(false);
  }, [resetAllProgress]);

  // Score breakdown component with WordMatchGameV2 styling
  const ScoreBreakdown = React.memo(() => {
    const accuracy = attempts > 0 ? Math.round((correctAttempts / attempts) * 100) : 0;
    
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl p-4 md:p-6 mb-6 border-4 border-pink-300">
        <h3 className="text-xl md:text-2xl font-bold text-center mb-4 text-purple-600">📊 Your Score!</h3>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white text-center">
            <div className="text-2xl md:text-3xl mb-1">🎯</div>
            <p className="font-bold text-lg md:text-xl">{totalScore}</p>
            <p className="text-xs font-bold">Score</p>
          </div>
          <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white text-center">
            <div className="text-2xl md:text-3xl mb-1">✅</div>
            <p className="font-bold text-lg md:text-xl">{correctAttempts}/{attempts}</p>
            <p className="text-xs font-bold">Correct</p>
          </div>
          <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white text-center">
            <div className="text-2xl md:text-3xl mb-1">📊</div>
            <p className="font-bold text-lg md:text-xl">{accuracy}%</p>
            <p className="text-xs font-bold">Accuracy</p>
          </div>
          <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white text-center">
            <div className="text-2xl md:text-3xl mb-1">🔥</div>
            <p className="font-bold text-lg md:text-xl">{consecutiveCorrect}</p>
            <p className="text-xs font-bold">Streak</p>
          </div>
        </div>
        {consecutiveCorrect >= 3 && (
          <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
            <p className="text-white font-bold text-lg animate-pulse">
              🔥 {consecutiveCorrect} in a row! You're on fire! 🔥
            </p>
          </div>
        )}
        <div className="mt-4">
          <button 
            onClick={handleResetProgress}
            className="w-full px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🗑️ Reset Progress
          </button>
        </div>
        <div className="mt-3">
          <Link
            to="/"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            ← Back to Games
          </Link>
        </div>
      </div>
    );
  });

  const OptionButton = React.memo(({ option, onClick, disabled, status }) => {
    const safeOption = option || {
      english: 'Unknown',
      arabic: 'غير معروف',
      isCorrect: false
    };

    let buttonClass = "w-full p-4 md:p-6 rounded-2xl font-bold text-lg md:text-xl transition-all duration-200 flex flex-col items-center border-4 ";
    
    if (status === 'playing') {
      buttonClass += "bg-gradient-to-r from-blue-100 to-blue-200 hover:from-blue-200 hover:to-blue-300 text-gray-800 border-blue-300 hover:border-blue-400 hover:shadow-xl transform hover:scale-105";
    } else if (safeOption.isCorrect) {
      buttonClass += "bg-gradient-to-r from-green-400 to-green-500 text-white shadow-xl border-green-300 animate-pulse";
    } else if (option?.id === selectedOption?.id && !safeOption.isCorrect) {
      buttonClass += "bg-gradient-to-r from-red-400 to-red-500 text-white border-red-300";
    } else {
      buttonClass += "bg-gray-200 text-gray-500 cursor-not-allowed border-gray-300";
    }

    return (
      <button
        onClick={() => onClick(safeOption)}
        disabled={disabled || status !== 'playing'}
        className={buttonClass}
        aria-label={`Select ${safeOption.english}`}
      >
        <span className="text-2xl md:text-3xl font-bold mb-2" dir="rtl" lang="ar">{safeOption.arabic}</span>
        <span className="text-lg md:text-xl">{safeOption.english}</span>
      </button>
    );
  });

  OptionButton.propTypes = {
    option: PropTypes.shape({
      id: PropTypes.string,
      english: PropTypes.string,
      arabic: PropTypes.string,
      isCorrect: PropTypes.bool
    }),
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    status: PropTypes.oneOf(['playing', 'correct', 'incorrect']).isRequired
  };

  // Modal components with WordMatchGameV2 styling
  const HintWarningModal = React.memo(() => {
    if (!showHintWarning) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-yellow-300">
          <div className="text-center">
            <div className="text-6xl md:text-8xl mb-4 md:mb-6">💡</div>
            <h2 className="text-2xl md:text-3xl font-bold text-yellow-600 mb-3 md:mb-4">Use Hint?</h2>
            <p className="text-gray-700 mb-2 text-sm md:text-lg">
              You will receive either a population or capital hint (randomly selected).
            </p>
            <p className="text-red-500 font-bold mb-6 md:mb-8 text-sm md:text-lg">
              ⚠️ Using this hint will deduct {SCORING_CONSTANTS.HINT_PENALTY} points from your score!
            </p>
            <div className="flex gap-3 md:gap-4 justify-center">
              <button 
                onClick={cancelHint}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Cancel
              </button>
              <button 
                onClick={useHint}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Use Hint (-{SCORING_CONSTANTS.HINT_PENALTY} pts)
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const LevelCompleteModal = React.memo(() => {
    if (!showLevelComplete) return null;
    
    const accuracy = Math.round(correctAttempts / Math.max(1, attempts)) * 100;
    const totalScoreWithBonuses = totalScore + timeBonus + accuracyBonus;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-4 md:p-8 border-4 border-green-300">
          <div className="text-center">
            <div className="text-6xl md:text-8xl mb-4 md:mb-6">🎉</div>
            <h2 className="text-2xl md:text-3xl font-bold text-green-600 mb-3 md:mb-4">
              Game Complete!
            </h2>
            <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
              You've identified all {countries.length} Arab countries!
            </p>

            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-4 md:p-6 mb-6 md:mb-8 border-4 border-purple-300">
              <div className="grid grid-cols-2 gap-4 text-center mb-4">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-3 text-white">
                  <p className="text-sm font-bold">Final Score</p>
                  <p className="text-2xl md:text-3xl font-bold">{totalScoreWithBonuses}</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-3 text-white">
                  <p className="text-sm font-bold">Accuracy</p>
                  <p className="text-2xl md:text-3xl font-bold">{accuracy}%</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-3 text-white">
                  <p className="text-sm font-bold">Time Bonus</p>
                  <p className="text-xl md:text-2xl font-bold">+{timeBonus}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-3 text-white">
                  <p className="text-sm font-bold">Accuracy Bonus</p>
                  <p className="text-xl md:text-2xl font-bold">+{accuracyBonus}</p>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="flex">
                  {[1, 2, 3].map(star => (
                    <span 
                      key={star} 
                      className={`text-3xl md:text-4xl ${star <= stars ? 'text-yellow-400' : 'text-gray-300'}`}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 md:gap-4 justify-center">
              <button 
                onClick={() => setShowLevelComplete(false)}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Continue
              </button>
              <button 
                onClick={() => {
                  setShowLevelComplete(false);
                  setCurrentCountryIndex(0);
                  setTotalScore(0);
                  setCorrectAttempts(0);
                  setAttempts(0);
                  setConsecutiveCorrect(0);
                }}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Play Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  const ResetConfirmationModal = React.memo(() => {
    if (!showResetConfirmation) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-4 md:p-8 border-4 border-red-300">
          <div className="text-center">
            <div className="text-6xl md:text-8xl mb-4 md:mb-6">⚠️</div>
            <h2 className="text-2xl md:text-3xl font-bold text-red-600 mb-3 md:mb-4">Reset Progress?</h2>
            <p className="text-gray-700 mb-6 md:mb-8 text-sm md:text-lg">
              This will reset your entire progress and scores.
              You'll start from scratch.
              This action cannot be undone!
            </p>
            <div className="flex gap-3 md:gap-4 justify-center">
              <button 
                onClick={() => setShowResetConfirmation(false)}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Cancel
              </button>
              <button 
                onClick={confirmResetProgress}
                className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold text-sm md:text-base hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                Yes, Reset Everything
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  });

  if (dataError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 max-w-md text-center shadow-2xl border-4 border-red-300">
          <div className="text-6xl mb-4">😢</div>
          <h2 className="text-2xl font-bold text-red-600 mb-4">Data Error</h2>
          <p className="text-gray-700 mb-6">{dataError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !currentCountry) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full border-4 border-purple-300">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-spin">🌟</div>
            <div className="text-purple-600 text-2xl font-bold animate-pulse">Loading Flag Explorer...</div>
            <div className="text-gray-600 text-lg mt-2">Getting ready for fun!</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 p-4">
      {/* Audio Elements */}
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Mobile Back to Games (top-left chip, mobile/tablet only) */}
        <div className="lg:hidden mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm text-purple-700 rounded-full font-bold text-sm border-2 border-white/70 shadow-lg hover:bg-white active:scale-95 transition-all duration-200"
          >
            <span className="text-lg leading-none">←</span>
            Back to Games
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-2 drop-shadow-lg">
            🏳️ Flag Explorer 🏳️
          </h1>
          <p className="text-xl md:text-2xl text-white opacity-90">
            Country {currentCountryIndex + 1} of {countries.length}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-white bg-opacity-30 rounded-full h-4 mb-6 border-2 border-white/50">
          <div 
            className="bg-gradient-to-r from-green-400 to-green-500 h-full rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
            style={{ width: `${((currentCountryIndex + (gameStatus !== 'playing' ? 1 : 0)) / countries.length) * 100}%` }}
          >
            <span className="text-white text-xs font-bold">
              {Math.round(((currentCountryIndex + (gameStatus !== 'playing' ? 1 : 0)) / countries.length) * 100)}%
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Score (Hidden on mobile, integrated on desktop) */}
          <div className="hidden lg:block">
            <ScoreBreakdown />
          </div>

          {/* Main Game Area */}
          <div className="lg:col-span-3">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-6 md:p-8 border-4 border-pink-300">
              <div className="text-center">
                {/* Flag Display */}
                <div className="mb-8 flex flex-col items-center">
                  <div className="w-64 md:w-80 h-40 md:h-48 border-4 border-purple-300 shadow-2xl mb-6 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl">
                    {!currentCountry?.flag ? (
                      <div className="text-gray-500 text-lg font-bold">Loading flag...</div>
                    ) : (
                      <img 
                        key={`flag-${currentCountryIndex}`}
                        src={currentCountry.flag} 
                        alt="Flag to guess"
                        className="max-h-full max-w-full object-contain rounded-xl"
                        onError={(e) => {
                          console.log('Flag failed to load:', currentCountry.flag);
                          e.target.onerror = null;
                          e.target.src = '/pics/flags/default.svg';
                        }}
                        onLoad={() => {
                          console.log('Flag loaded successfully:', currentCountry.flag);
                        }}
                      />
                    )}
                  </div>
                  <button 
                    onClick={() => currentCountry.audio?.country?.url && playSoundFromUrl(currentCountry.audio.country.url)}
                    className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                    disabled={!currentCountry.audio?.country?.url}
                  >
                    🔊 Hear Country Name
                  </button>
                </div>

                {/* Mobile Score Display */}
                <div className="lg:hidden mb-6">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 border-4 border-pink-300">
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-2 text-white">
                        <div className="text-xl mb-1">🎯</div>
                        <p className="font-bold text-sm">{totalScore}</p>
                        <p className="text-xs">Score</p>
                      </div>
                      <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-2 text-white">
                        <div className="text-xl mb-1">✅</div>
                        <p className="font-bold text-sm">{correctAttempts}/{attempts}</p>
                        <p className="text-xs">Correct</p>
                      </div>
                      <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-2 text-white">
                        <div className="text-xl mb-1">📊</div>
                        <p className="font-bold text-sm">{attempts > 0 ? Math.round((correctAttempts / attempts) * 100) : 0}%</p>
                        <p className="text-xs">Accuracy</p>
                      </div>
                      <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-2 text-white">
                        <div className="text-xl mb-1">🔥</div>
                        <p className="font-bold text-sm">{consecutiveCorrect}</p>
                        <p className="text-xs">Streak</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hint Button */}
                <div className="flex justify-center mb-6">
                  <button 
                    onClick={handleHintRequest}
                    disabled={usedHints >= 2 || gameStatus !== 'playing'}
                    className="px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-2xl font-bold text-lg md:text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    💡 Use Hint ({2 - usedHints} left)
                  </button>
                </div>

                {/* Hint Display */}
                {showHint && (
                  <div className="mb-6 p-4 md:p-6 bg-gradient-to-r from-yellow-100 to-orange-100 border-4 border-yellow-400 rounded-2xl">
                    <p className="text-lg md:text-xl font-bold text-yellow-800">{currentHint}</p>
                  </div>
                )}

                {/* Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
                  {options.map(option => (
                    <OptionButton
                      key={option.id}
                      option={option}
                      onClick={handleOptionSelect}
                      disabled={selectedOption !== null}
                      status={gameStatus}
                    />
                  ))}
                </div>

                {/* Game Status Feedback */}
                {gameStatus === 'correct' && (
                  <div className="text-2xl md:text-3xl font-bold text-green-600 mb-4 animate-bounce bg-green-100 p-4 rounded-2xl border-4 border-green-300">
                    🎉 Correct! This is {currentCountry.names.english}
                  </div>
                )}
                {gameStatus === 'incorrect' && (
                  <div className="text-2xl md:text-3xl font-bold text-red-600 mb-4 bg-red-100 p-4 rounded-2xl border-4 border-red-300">
                    ❌ Incorrect! This is {currentCountry.names.english}
                  </div>
                )}

                {/* Celebration Effects */}
                {celebrations && (
                  <div className="fixed inset-0 pointer-events-none z-40">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-9xl animate-bounce">🎉</div>
                    </div>
                  </div>
                )}

                {sparkles && (
                  <div className="fixed inset-0 pointer-events-none z-40">
                    {[...Array(15)].map((_, i) => (
                      <div
                        key={`sparkle-${i}`}
                        className="absolute animate-ping text-yellow-400 text-2xl"
                        style={{
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                          animationDelay: `${Math.random() * 2}s`
                        }}
                      >
                        ✨
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <HintWarningModal />
      <LevelCompleteModal />
      <ResetConfirmationModal />
    </div>
  );
};

ArabicFlagGuesser.propTypes = {
  levelsData: PropTypes.objectOf(
    PropTypes.shape({
      names: PropTypes.shape({
        english: PropTypes.string.isRequired,
        arabic: PropTypes.string.isRequired,
        pronunciation: PropTypes.string
      }).isRequired,
      countryCode: PropTypes.string.isRequired,
      capital: PropTypes.shape({
        english: PropTypes.string,
        arabic: PropTypes.string
      }),
      population: PropTypes.number,
      flag: PropTypes.string,
      audio: PropTypes.shape({
        country: PropTypes.shape({
          url: PropTypes.string
        }),
        capital: PropTypes.shape({
          url: PropTypes.string
        })
      })
    })
  )
};

export default ArabicFlagGuesser;